// src/hooks/useWallet.js
import { useEffect, useMemo, useState } from 'react';
import { useAccount, useChainId, useDisconnect } from 'wagmi';

function normalizeChainId(value) {
  if (value == null) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const raw = String(value).trim();
  if (!raw) return 0;
  if (/^0x/i.test(raw)) {
    const parsed = Number.parseInt(raw, 16);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeAccounts(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || '').trim().toLowerCase())
    .filter(Boolean);
}

export function formatAddress(address, start = 6, end = 4) {
  if (!address || typeof address !== 'string') return '';
  if (address.length <= start + end) return address;
  return address.slice(0, start) + '...' + address.slice(-end);
}

export default function useWallet() {
  const account = useAccount();
  const wagmiChainId = useChainId();
  const { disconnect } = useDisconnect();
  const [providerChainId, setProviderChainId] = useState(0);
  const [providerAddress, setProviderAddress] = useState('');

  const accountAddress = String(account?.address || '').trim();
  const connector = account?.connector || null;
  const isPhantomConnector = useMemo(() => {
    const connectorId = String(connector?.id || '').toLowerCase();
    const connectorName = String(connector?.name || '').toLowerCase();
    return connectorId.includes('phantom') || connectorName.includes('phantom');
  }, [connector]);
  const address = accountAddress || providerAddress;
  const isConnected = !!(account?.isConnected || (isPhantomConnector && address));

  useEffect(() => {
    let cancelled = false;

    const resolvedAccountChainId = normalizeChainId(account?.chainId);
    const resolvedWagmiChainId = normalizeChainId(wagmiChainId);
    const immediateChainId = resolvedAccountChainId || resolvedWagmiChainId;

    if (!connector) {
      setProviderChainId(0);
      setProviderAddress('');
      return undefined;
    }

    if (immediateChainId > 0) {
      setProviderChainId((prev) => (prev === immediateChainId ? prev : immediateChainId));
    }

    const resolveFromProvider = async () => {
      try {
        const provider = await connector.getProvider?.();
        if (!provider?.request) {
          if (!cancelled) {
            if (!immediateChainId) setProviderChainId(0);
            setProviderAddress('');
          }
          return;
        }

        let nextChainId = 0;
        try {
          nextChainId = normalizeChainId(await provider.request({ method: 'eth_chainId' }));
        } catch {}

        if (!nextChainId) {
          try {
            nextChainId = normalizeChainId(await provider.request({ method: 'net_version' }));
          } catch {}
        }

        let nextAddress = '';
        try {
          const accounts = normalizeAccounts(await provider.request({ method: 'eth_accounts' }));
          nextAddress = accounts[0] || '';
        } catch {}

        if (!cancelled) {
          if (nextChainId || !immediateChainId) {
            setProviderChainId(nextChainId || 0);
          }
          setProviderAddress(nextAddress);
        }
      } catch {
        if (!cancelled) {
          if (!immediateChainId) setProviderChainId(0);
          setProviderAddress('');
        }
      }
    };

    resolveFromProvider();

    return () => {
      cancelled = true;
    };
  }, [account?.chainId, wagmiChainId, connector]);

  const chainId = useMemo(() => {
    const accountChainId = Number(account?.chainId || 0);
    const wagmiResolvedChainId = Number(wagmiChainId || 0);
    return accountChainId || wagmiResolvedChainId || Number(providerChainId || 0) || 0;
  }, [account?.chainId, wagmiChainId, providerChainId]);

  return {
    // core
    address,
    isConnected,
    chainId,
    networkId: chainId,

    // extras (handy later)
    connector,
    status: account?.status || 'unknown',

    // UI helper
    shortAddress: formatAddress(address),

    // actions
    disconnect,
  };
}
